// The typed parts of a ClintoBytes Copilot reply (gap analysis §4.2a). Each
// part is rendered as a component, never as free markdown: numbers in a data
// card come straight from the tool rows the server returned, not from the
// model's sentence.
import { lazy, Suspense, useState } from 'react';
import { AlertTriangle, ArrowRight, ChevronDown, ChevronRight, Lock, Send } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { ReferenceAnswerView } from '@/features/clinical-references/ReferenceAnswerView';
import type { CopilotAction, CopilotCard, CopilotCell, CopilotLink, CopilotPart } from './api';

// recharts is loaded only when a chart is actually shown.
const MetricChart = lazy(() => import('./MetricChart'));

const rupees = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const cellText = (c: CopilotCell) => (c && typeof c === 'object' ? rupees(c.money) : c ?? '—');

export interface PartHandlers {
  onNavigate: (link: CopilotLink) => void;
  onAsk: (text: string) => void;
  onHandoff: () => void;
  onConfirmAction: (action: CopilotAction) => Promise<void>;
  onCancelAction: () => void;
  actionState: 'prepared' | 'confirmed' | 'cancelled' | 'none';
}

function linkLabel(l: CopilotLink, fallback: string) { return l.label || fallback; }

function DataCard({ card, onNavigate }: { card: CopilotCard; onNavigate: PartHandlers['onNavigate'] }) {
  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border bg-muted px-3 py-1.5 text-[12px] font-semibold text-ink">{card.title}</div>
      {!!card.kpis?.length && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 px-3 py-2">
          {card.kpis.map((k) => (
            <div key={k.label} className="min-w-0">
              <div className="text-[11px] text-ink-muted">{k.label}</div>
              <div className="text-[15px] font-semibold text-ink">{k.money ? rupees(Number(k.value)) : k.value}</div>
            </div>
          ))}
        </div>
      )}
      {card.chart && (
        <Suspense fallback={<div className="h-36" />}>
          <MetricChart chart={card.chart} />
        </Suspense>
      )}
      {!!card.rows?.length && (
        <div className={cn('overflow-auto', card.chart ? 'max-h-40 border-t border-border' : 'max-h-56')}>
          <table className="w-full text-[12px]">
            {card.columns && (
              <thead className="sticky top-0 bg-card">
                <tr>{card.columns.map((c) => <th key={c} className="px-3 py-1 text-left font-medium text-ink-muted">{c}</th>)}</tr>
              </thead>
            )}
            <tbody>
              {card.rows.map((r, i) => {
                const link = card.links?.[i];
                return (
                  <tr key={i} className={cn('border-t border-border', link && 'cursor-pointer hover:bg-muted')}
                    onClick={link ? () => onNavigate(link) : undefined}>
                    {r.map((c, j) => <td key={j} className="px-3 py-1 text-ink">{cellText(c)}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {card.rows && !card.rows.length && <div className="px-3 py-2 text-[12px] text-ink-muted">Nothing to show.</div>}
      {card.note && <div className="px-3 pb-2 text-[11px] text-ink-muted">{card.note}</div>}
      {card.open && (
        <div className="border-t border-border px-3 py-1.5 text-right">
          <button type="button" className="inline-flex items-center gap-1 text-[12px] font-medium text-teal hover:underline" onClick={() => onNavigate(card.open!)}>
            Open {linkLabel(card.open, 'screen')} <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function ActionCard({ action, h }: { action: CopilotAction; h: PartHandlers }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = h.actionState === 'confirmed' || h.actionState === 'cancelled';
  return (
    <div className="mt-2 rounded-lg border-2 border-warn bg-warn-soft p-3">
      <div className="flex items-center gap-1.5 text-[12px] font-semibold text-warn-ink"><AlertTriangle className="h-3.5 w-3.5" /> Confirm: {action.title}</div>
      {action.lines.map((l) => <div key={l} className="mt-1 text-[13px] text-ink">{l}</div>)}
      {error && <div className="mt-2 text-[12px] text-danger">{error}</div>}
      {done ? (
        <div className="mt-2 text-[12px] font-medium text-ink-muted">{h.actionState === 'confirmed' ? '✓ Done' : 'Cancelled'}</div>
      ) : (
        <div className="mt-2 flex justify-end gap-2">
          <Button size="sm" variant="ghost" disabled={busy} onClick={h.onCancelAction}>Cancel</Button>
          <Button size="sm" loading={busy} onClick={async () => {
            setBusy(true); setError(null);
            try { await h.onConfirmAction(action); } catch (e) { setError((e as Error).message || 'That did not work'); } finally { setBusy(false); }
          }}>{action.confirm_label}</Button>
        </div>
      )}
    </div>
  );
}

function Disclosure({ part }: { part: Extract<CopilotPart, { type: 'disclosure' }> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1.5 text-[11px] text-ink-muted">
      <button type="button" className="inline-flex items-center gap-0.5 hover:text-ink" onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} How I got this
      </button>
      {open && (
        <ul className="ml-4 mt-0.5 list-disc">
          {part.tools.map((t, i) => (
            <li key={i}>{t.label}{t.count != null ? ` · ${t.count} result${t.count === 1 ? '' : 's'}` : ''}{t.ok ? '' : ' · not used'}</li>
          ))}
          {part.branch && <li>Branch: {part.branch}</li>}
          <li>Read with your own permissions</li>
        </ul>
      )}
    </div>
  );
}

export function CopilotParts({ parts, h }: { parts: CopilotPart[]; h: PartHandlers }) {
  return (
    <>
      {parts.map((p, i) => {
        switch (p.type) {
          case 'text': return <p key={i} className="whitespace-pre-wrap text-[13px] leading-relaxed text-ink">{p.text}</p>;
          case 'data': return <DataCard key={i} card={p.card} onNavigate={h.onNavigate} />;
          case 'navigate':
            return (
              <button key={i} type="button" onClick={() => h.onNavigate(p)}
                className="mt-2 inline-flex items-center gap-1 rounded-full border border-teal px-3 py-1 text-[12px] font-medium text-teal hover:bg-teal-light">
                Go to {p.label} <ArrowRight className="h-3 w-3" />
              </button>
            );
          case 'help':
            return p.articles.map((a, j) => (
              <div key={`${i}-${j}`} className="mt-2 rounded-lg border border-border bg-card p-3">
                <div className="text-[12px] font-semibold text-ink">{a.title}</div>
                <ol className="ml-4 mt-1 list-decimal text-[12px] text-ink">{a.steps.map((s) => <li key={s}>{s}</li>)}</ol>
                {a.navigate && (
                  <button type="button" className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium text-teal hover:underline" onClick={() => h.onNavigate(a.navigate!)}>
                    Take me there <ArrowRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            ));
          case 'clarify':
            return (
              <div key={i} className="mt-2">
                <div className="text-[12px] text-ink-muted">{p.question}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {p.options.map((o) => (
                    <button key={o.value} type="button" onClick={() => h.onAsk(`The patient with UHID ${o.value}`)}
                      className="rounded-full border border-border bg-card px-2.5 py-1 text-[12px] text-ink hover:border-teal">{o.label}</button>
                  ))}
                </div>
              </div>
            );
          case 'action': return <ActionCard key={i} action={p.action} h={h} />;
          case 'references':
            return (
              <div key={i} className="mt-2 rounded-lg border border-border bg-card p-3">
                <ReferenceAnswerView answer={p.result} compact />
              </div>
            );
          case 'refused':
            return <div key={i} className="mt-2 flex items-center gap-1.5 text-[12px] text-ink-muted"><Lock className="h-3 w-3" /> You don't have access to {p.label}.</div>;
          case 'handoff':
            return (
              <button key={i} type="button" onClick={h.onHandoff}
                className="mt-2 inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-[12px] font-medium text-ink hover:border-teal">
                <Send className="h-3 w-3" /> Send this to the ClintoBytes team
              </button>
            );
          case 'disclosure': return <Disclosure key={i} part={p} />;
          default: return null;
        }
      })}
    </>
  );
}
